import { StringDevUIField } from "./StringDevUIField";
import { resolveDevtoolsIcon } from "../../StringDevIconRegistry";

const HOLD_DELAY_MS = 320;
const HOLD_INTERVAL_MS = 60;
const DRAG_PIXELS_PER_STEP = 6;

export class StringDevFieldNumber extends StringDevUIField<number> {
  private min: number;
  private max: number;
  private step: number;
  private input: HTMLInputElement | null = null;
  private stepperButtons: HTMLButtonElement[] = [];

  constructor(
    key: string,
    label: string,
    defaultValue: number,
    onChange: (key: string, value: number) => void,
    min: number = 0,
    max: number = 100,
    step: number = 1,
  ) {
    super(key, label, defaultValue, onChange);
    this.min = min;
    this.max = max;
    this.step = step;
  }

  build(): HTMLElement {
    const row = this.createRow();
    row.appendChild(this.createLabel());
    row.appendChild(this.createControls());
    this.root = row;
    return row;
  }

  setValue(value: number): void {
    this.value = value;
    if (this.input) this.input.value = this.formatValue(value);
  }

  override setDisabled(disabled: boolean): void {
    super.setDisabled(disabled);
    if (this.input) this.input.disabled = disabled;
    for (const button of this.stepperButtons) {
      button.disabled = disabled;
    }
  }

  private createControls(): HTMLDivElement {
    const control = document.createElement("div");
    control.setAttribute("data-stdg-field-input", "");

    const stepper = document.createElement("div");
    stepper.setAttribute("data-stdg-stepper", "");
    stepper.appendChild(this.createStepperButton("up", 1));
    stepper.appendChild(this.createStepperButton("down", -1));

    control.appendChild(this.createInput());
    control.appendChild(stepper);
    return control;
  }

  private createInput(): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "text";
    input.inputMode = "text";
    input.setAttribute("enterkeyhint", "done");
    input.autocomplete = "off";
    input.spellcheck = false;
    input.value = this.formatValue(this.value);
    input.setAttribute("data-stdg-input", "");

    input.addEventListener("focus", () => {
      input.select();
    });
    input.addEventListener("blur", () => {
      this.commitValue(input.value);
    });
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        input.blur();
        return;
      }
      if (event.key === "Escape") {
        input.value = this.formatValue(this.value);
        input.blur();
        return;
      }
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      this.applyDelta(event.key === "ArrowUp" ? 1 : -1, event);
    });
    input.addEventListener("wheel", (event) => {
      if (document.activeElement !== input) return;
      event.preventDefault();
      this.applyDelta(event.deltaY < 0 ? 1 : -1, event);
    });
    input.addEventListener("mousedown", (event) => {
      if (event.button !== 1) return;
      this.startMiddleDrag(event, input);
    });
    input.addEventListener("auxclick", (event) => {
      if (event.button === 1) event.preventDefault();
    });

    this.input = input;
    return input;
  }

  private createStepperButton(directionName: "up" | "down", direction: 1 | -1): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-stdg-button", "");
    button.setAttribute("data-stdg-button-icon-12", "");
    button.setAttribute("data-direction", directionName);
    button.innerHTML = resolveDevtoolsIcon(
      12,
      directionName === "up" ? "chevrone-up" : "chevrone-down",
    );

    let holdTimeout = 0;
    let holdInterval = 0;
    let suppressClick = false;

    const stopHold = () => {
      window.clearTimeout(holdTimeout);
      window.clearInterval(holdInterval);
      document.removeEventListener("pointerup", stopHold);
      document.removeEventListener("pointercancel", stopHold);
    };

    button.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;

      holdTimeout = window.setTimeout(() => {
        suppressClick = true;
        this.applyDelta(direction);
        holdInterval = window.setInterval(() => {
          this.applyDelta(direction);
        }, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);

      document.addEventListener("pointerup", stopHold);
      document.addEventListener("pointercancel", stopHold);
    });

    button.addEventListener("click", (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        return;
      }

      this.applyDelta(direction);
      this.input?.focus();
      this.input?.select();
    });

    this.stepperButtons.push(button);
    return button;
  }

  private startMiddleDrag(event: MouseEvent, input: HTMLInputElement): void {
    event.preventDefault();

    let previousX = event.clientX;
    let accumulated = 0;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    const stopDrag = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDrag);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - previousX;
      previousX = moveEvent.clientX;
      accumulated += deltaX;

      while (Math.abs(accumulated) >= DRAG_PIXELS_PER_STEP) {
        const direction = accumulated > 0 ? 1 : -1;
        this.applyDelta(direction, moveEvent);
        accumulated -= DRAG_PIXELS_PER_STEP * direction;
      }
    };

    input.focus();
    input.select();
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDrag);
  }

  private commitValue(rawValue: string): void {
    const parsed = parseFloat(rawValue.replace(",", "."));
    if (isNaN(parsed)) {
      if (this.input) this.input.value = this.formatValue(this.value);
      return;
    }

    const nextValue = this.normalizeValue(parsed);
    if (this.input) this.input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private applyDelta(direction: 1 | -1, event?: KeyboardEvent | WheelEvent | MouseEvent): void {
    const multiplier = event?.shiftKey ? 10 : event?.altKey ? 0.1 : 1;
    const nextValue = this.normalizeValue(this.value + direction * this.step * multiplier);
    if (this.input) this.input.value = this.formatValue(nextValue);
    this.emit(nextValue);
  }

  private normalizeValue(value: number): number {
    const snapped = Math.round(value / this.step) * this.step;
    const precision = this.getPrecision(this.step);
    return Number(snapped.toFixed(precision));
  }

  private getPrecision(value: number): number {
    const parts = String(value).split(".");
    return parts[1]?.length ?? 0;
  }

  private formatValue(value: number): string {
    const precision = this.getPrecision(this.step);
    return precision > 0 ? value.toFixed(precision) : String(value);
  }
}
